import { useEffect, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { navTo } from "../components/ContextChain";

/**
 * MyWork — workspace-level (app-independent) cross-engagement landing. Lists the
 * user's engagements for the active workspace from the REAL backend
 * (GET /api/workspace/engagements?workspace_id=…), with click-to-open. No stubs;
 * honest empty-state until engagements exist.
 *
 * The engagement route is client-scoped (ADR-08). The engagement's client is now
 * DERIVED by the backend (spec 20, data-attachment arc 1) and returned as `client_id`,
 * so deep-links use the real client — no ContextChain fallback, no fabricated default.
 * An UNPLACED engagement (`client_id` null) cannot form a client-scoped deep link; it is
 * surfaced honestly and opens the workspace home instead.
 */
export interface MyWorkEngagement {
  id: string;
  workspace_id: string;
  title: string;
  status: string;
  client_id?: string | null;
}

export interface MyWorkPageProps {
  apiBase?: string;
  fetchImpl?: typeof fetch;
}

type ViewState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; engagements: MyWorkEngagement[] };

export function MyWorkPage({ apiBase = "/api/workspace", fetchImpl }: MyWorkPageProps): React.ReactElement {
  const { ws } = useParams({ strict: false }) as { ws?: string };
  const navigate = useNavigate();
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const openEngagement = (e: MyWorkEngagement) => {
    if (!ws) return;
    if (e.client_id) {
      navigate(navTo.engagementHome(ws, e.client_id, e.id) as never);
    } else {
      // Honest null-client handling (spec 20 D-A5): no client → no client-scoped deep link.
      // Land at the workspace home rather than invent a default client.
      navigate(navTo.workspaceHome(ws) as never);
    }
  };

  useEffect(() => {
    const f = fetchImpl ?? fetch;
    const base = apiBase.replace(/\/$/, "");
    let cancelled = false;
    (async () => {
      try {
        const qs = ws ? `?workspace_id=${encodeURIComponent(ws)}` : "";
        const res = await f(`${base}/engagements${qs}`, { method: "GET" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as MyWorkEngagement[];
        if (!cancelled) setState({ kind: "ready", engagements: data });
      } catch (e) {
        if (!cancelled) {
          setState({ kind: "error", message: e instanceof Error ? e.message : String(e) });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase, fetchImpl, ws]);

  return (
    <div className="py-1" data-app="mywork">
      <p className="px-3 pt-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
        My Work
      </p>

      {state.kind === "loading" && (
        <div role="status" className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">
          Loading engagements…
        </div>
      )}
      {state.kind === "error" && (
        <div role="alert" className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">
          Couldn’t load engagements: {state.message}
        </div>
      )}
      {state.kind === "ready" &&
        (state.engagements.length === 0 ? (
          <div className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">No engagements yet.</div>
        ) : (
          <ul className="flex flex-col">
            {state.engagements.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  onClick={() => openEngagement(e)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-surface-muted)]"
                >
                  <span className="truncate">{e.title}</span>
                  <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">
                    {e.client_id ? e.status : `${e.status} · unplaced`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
}
